import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { isAuthenticated, login, logout as logoutSession } from '../services/authService';
import { syncSessionToExtensionSilently } from '../services/extensionSyncService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      if (isAuthenticated()) {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
          setUser(JSON.parse(userInfo));
        }
        // Utilisateur déjà connecté au chargement : pousser la session à l'extension,
        // qui a pu être installée depuis la dernière connexion.
        syncSessionToExtensionSilently();
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const updateUserProfile = async (profileData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put('/users/profile', profileData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error updating profile');
    }
  };

  const loadUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/users/profile', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const userProfile = response.data;
      setUser(userProfile);
      localStorage.setItem('userInfo', JSON.stringify(userProfile));
      window.dispatchEvent(new Event('localStorageUpdated'));
      return userProfile;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error loading profile');
    }
  };

  const updatePaymentStatus = (paymentRequired = false) => {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      const updatedUser = {
        ...JSON.parse(userInfo),
        payment_required: paymentRequired
      };
      setUser(updatedUser);
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
    }
  };

  const logout = () => {
    // Délégué à authService pour purger aussi la session de l'extension.
    logoutSession();
    setUser(null);
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/register', userData);
      
      if (response.status === 201) {
        // Vérifier si le paiement est requis
        if (response.data.requiresPayment && response.data.paymentIntent) {
          // Si paiement requis, retourner toutes les données du backend
          return {
            success: true,
            requiresPayment: response.data.requiresPayment,
            paymentIntent: response.data.paymentIntent,
            subscription: response.data.subscription,
            user_id: response.data.user_id,
            message: response.data.message || 'Inscription créée, paiement requis'
          };
        }
        
        // Si pas de paiement requis, tenter la connexion automatique
        try {
          // Utilisation de la fonction login importée de authService
          const loginResult = await login(userData.email, userData.password, true);
          if (loginResult) {
            return {
              success: true,
              message: 'Inscription réussie'
            };
          }
        } catch (loginError) {
          return {
            success: true,
            message: 'Inscription réussie. Veuillez vous connecter.',
            requireLogin: true
          };
        }
      }
      
      return {
        success: false,
        message: response.data.message || 'Erreur lors de l\'inscription',
        violations: response.data.violations
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de l\'inscription';
      const violations = error.response?.data?.violations || [];
      
      throw {
        message: errorMessage,
        violations: violations
      };
    }
  };

  const value = {
    user,
    setUser,
    updateUserProfile,
    loadUserProfile,
    updatePaymentStatus,
    logout,
    register,
    login
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
